import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { Icon, TIconType } from '@components/icons';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import { useLocales } from '@hooks/helpers/useLocales';

interface IProps {
    onChange?: (value?: File) => void;
    onTakePhoto?: () => void;
    preview?: string;
    label?: string;
    isUpload?: boolean;
    isPreview?: boolean;
    isAcceptImages?: boolean;
    isCamera?: boolean;
    isDraggable?: boolean;
    icon?: TIconType;
    disabled?: boolean;
    description?: string;
    accept?: string[];
    isShowDescription?: boolean;
    size?: number; // in Mb
}

const DragAndDrop: FC<IProps> = ({
    onChange,
    preview,
    isDraggable,
    accept,
    disabled,
    size,
    isCamera = true,
    isUpload = true,
    onTakePhoto,
    label,
    isAcceptImages,
    isPreview,
    icon,
    description,
    isShowDescription = true
}) => {
    const { submitLocale } = useLocales();
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const dropRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (!disabled) {
            e.preventDefault();
            e.stopPropagation();
            if (dropRef.current) {
                dropRef.current.classList.add(styles.dragOver);
            }
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!disabled) {
            e.preventDefault();
            e.stopPropagation();
            if (dropRef.current) {
                dropRef.current.classList.remove(styles.dragOver);
            }
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (!disabled) {
            e.preventDefault();
            e.stopPropagation();
            if (dropRef.current) {
                dropRef.current.classList.remove(styles.dragOver);
            }
            const file = e.dataTransfer.files[0];
            if (file && file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (event: ProgressEvent<FileReader>) => {
                    if (event.target && event.target.result) {
                        setImageSrc(event.target.result as string);
                    }
                };
                reader.readAsDataURL(file);
            }
            onChange && onChange(file);
        }
    };

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onload = (event: ProgressEvent<FileReader>) => {
                if (event.target && event.target.result) {
                    setImageSrc(event.target.result as string);
                }
            };
            reader.readAsDataURL(file);
            onChange && onChange(file);
        }
    }, []);

    useEffect(() => {
        if (preview) {
            setImageSrc(preview);
        }
    }, [preview]);

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    if (isPreview && imageSrc) {
        return (
            <div className={styles.preview_wrapper}>
                <img className={styles.preview} src={imageSrc} alt="" />
                <button
                    className={styles.delete}
                    onClick={() => {
                        setImageSrc(null);
                        onChange && onChange(undefined);
                    }}>
                    <Icon name="delete" />
                </button>
            </div>
        );
    }

    return (
        <div
            className={`${styles.wrapper} ${isDraggable ? styles.isDraggable : ''}`}
            ref={dropRef}
            onDragOver={(e) => isDraggable && handleDragOver(e)}
            onDragLeave={(e) => isDraggable && handleDragLeave(e)}
            onDrop={(e) => isDraggable && handleDrop(e)}>
            <Icon name={icon || 'filled_camera'} width={48} height={48} />
            <Typography className={styles.title} variant="body4" alignment="center">
                {label ||
                    'Take a photo with your camera, drag and drop it here or select photo to upload'}
            </Typography>
            {isShowDescription && (
                <Typography variant="caption1" className={styles.description} alignment="center">
                    {description ||
                        'It should be smaller than 2MB. Please make sure your photo clearly shows your face'}
                </Typography>
            )}
            <div className={styles.actions}>
                {isCamera && (
                    <Button variant="outlined-gold" size="small" onClick={onTakePhoto}>
                        {submitLocale('take_photo')}
                    </Button>
                )}
                {isUpload && (
                    <Button variant="outlined-gold" size="small" onClick={handleButtonClick}>
                        {submitLocale('select_photo')}
                    </Button>
                )}
            </div>
            {isAcceptImages && (
                <Typography className={styles.accept} variant="caption1" alignment="center">
                    You can upload multiple{' '}
                    {accept
                        ? accept.map(
                              (item, i) =>
                                  `.${item.split('/')?.[1]}${accept.length - 1 > i ? ', ' : ''}`
                          )
                        : '.png, .jpeg, .jpg'}
                    . Max file size: {size || 10}MB
                </Typography>
            )}
            <input
                type="file"
                accept={accept ? accept?.join(', ') : 'image/*'}
                size={(size ? size : 10) / (1024 * 1024)}
                ref={fileInputRef}
                disabled={disabled}
                onChange={handleFileSelect}
                className={styles.fileInput}
            />
        </div>
    );
};

export default DragAndDrop;
